<script setup lang="ts">
type Size = 'small' | 'medium' | 'big';

interface IProps {
    size?: Size;
    label?: string;
    activeLabel?: string;
    inactiveLabel?: string;
    disabled?: boolean;
    readOnly?: boolean;
    rightLabel?: boolean;
}
const {
    size = 'medium',
    label = '',
    activeLabel = '',
    inactiveLabel = '',
    disabled,
    readOnly,
    rightLabel,
} = defineProps<IProps>();
const modelValue = defineModel<boolean>({ default: false });
const native = ref<HTMLInputElement>();

const localLabel = computed(() => {
    if (activeLabel && inactiveLabel) {
        return modelValue.value ? activeLabel : inactiveLabel;
    } else {
        return label;
    }
});
function restart() {
    if (native.value) {
        native.value.checked = modelValue.value;
    }
}

defineExpose({
    restart,
});
</script>

<template>
    <div
        class="mr-switch-container"
        :class="{ 'right-label': rightLabel, disabled }"
        @click.stop
    >
        <label class="switch" :class="[size]">
            <input
                id="input-checkbox"
                ref="native"
                v-model="modelValue"
                class="input"
                type="checkbox"
                :disabled="disabled || readOnly"
            />
            <span class="slider" :class="{ disabled }" />
        </label>
        <div v-if="localLabel" class="label">
            {{ localLabel }}
        </div>
    </div>
</template>

<style scoped lang="scss">
.mr-switch-container {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    &.right-label {
        flex-direction: row-reverse;
        .label {
            margin-right: 0;
            margin-left: 8px;
        }
    }

    &.disabled {
        .switch {
            .slider {
                background: usePalette($p-gray, $p-300) !important;
            }
            input:checked + .slider {
                background-color: usePalette($p-gray, $p-500) !important;
            }
        }
        .label {
            color: usePalette($p-gray, $p-600);
        }
    }
}

// eslint-disable-next-line vue-scoped-css/no-unused-selector,vue-scoped-css/require-selector-used-inside
.switch {
    position: relative;
    display: inline-block;

    &.small {
        width: 28px;
        height: 16px;
        .slider:before {
            height: 12px;
            width: 12px;
        }
    }
    &.medium {
        width: 36px;
        height: 20px;
        .slider:before {
            height: 16px;
            width: 16px;
        }
    }
    &.big {
        width: 44px;
        height: 24px;
        .slider:before {
            height: 20px;
            width: 20px;
        }
    }

    input {
        visibility: hidden;
    }

    input:checked + .slider {
        background-color: usePalette();
        &:hover {
            background-color: usePalette($p-blue, $p-500);
        }
    }

    input:checked + .slider:before {
        -webkit-transform: translateX(calc(100%));
        -ms-transform: translateX(calc(100%));
        transform: translateX(calc(100%));
    }
}
/* The slider */
.slider {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: usePalette($p-blue, $p-200);
    -webkit-transition: 0.2s;
    transition: 0.2s;
    border-radius: 16px;
    &.disabled {
        cursor: not-allowed;
    }

    &:hover {
        background: usePalette($p-blue, $p-300);
    }
}

.slider:before {
    position: absolute;
    content: '';
    left: 2px;
    bottom: 2px;
    background-color: white;
    -webkit-transition: 0.2s;
    transition: 0.2s;
    border-radius: 50%;
}

.label {
    @include tg-l($w5, usePalette());
    margin-right: 8px;
}
</style>
